import {
  EmbedSettingsResponseDto,
  JobOfferEmbedDetailsShowingV2ResponseDto,
  JobOfferEmbedListShowingResponseDto,
  ProcessCard,
} from "@/lib/interfaces/embed";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useApiRequest from "../../../lib/hooks/useRequest";
import type { IframeSettingsDesignDraft, IframeSettingsTextDraft } from "../IframeSettingsPreviewContext";
import { getMockJobPostV2 } from "../job-posts-v2-mocks";
import { mergeJobPreviewPayload } from "../merge-job-preview-payload";

/** Non-production only: add `?job_posts_v2_debug=1` to use delayed mock data instead of the API. */
export function useJobPostsV2DebugEnabled(): boolean {
  const forceDebug = false;
  return forceDebug || (process.env.NODE_ENV !== "production" && typeof window !== "undefined" && new URLSearchParams(window.location.search).get("job_posts_v2_debug") === "1");
}

export function useJobPostsV2Source({
  organizationId,
  id,
  previewRawDetail,
  previewDesign,
  previewTextDraft,
  debug,
}: {
  organizationId: string;
  id: string;
  previewRawDetail?: JobOfferEmbedDetailsShowingV2ResponseDto | null;
  previewDesign?: IframeSettingsDesignDraft;
  previewTextDraft?: IframeSettingsTextDraft;
  debug: boolean;
}) {
  const { apiRequest } = useApiRequest();
  const apiRequestRef = useRef(apiRequest);
  apiRequestRef.current = apiRequest;

  const isLiveSettingsPreview =
    previewRawDetail != null && previewDesign != null && previewTextDraft != null;

  const mergedLivePreview = useMemo(() => {
    if (!isLiveSettingsPreview) return undefined;
    return mergeJobPreviewPayload(previewRawDetail!, previewDesign!, previewTextDraft!);
  }, [isLiveSettingsPreview, previewDesign, previewRawDetail, previewTextDraft]);

  const [jobPost, setJobPost] = useState<JobOfferEmbedDetailsShowingV2ResponseDto | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(() => !isLiveSettingsPreview);
  const [embedBaseSettings, setEmbedBaseSettings] = useState<EmbedSettingsResponseDto | null>(null);

  const isPreviewMode = id === "preview";

  const sourceJobPost = mergedLivePreview ?? jobPost;

  const buildV2PreviewFromQueryParams = useCallback((): JobOfferEmbedDetailsShowingV2ResponseDto => {
    const urlParams = new URLSearchParams(window.location.search);
    const previewProcessTitle = urlParams.get("processTitle");
    const previewProcessDescription = urlParams.get("processDescription");
    const previewProcessCards = urlParams.get("processCards");

    const parsedProcessCards: ProcessCard[] | undefined = previewProcessCards
      ? (() => {
        try {
          return JSON.parse(decodeURIComponent(previewProcessCards)) as ProcessCard[];
        } catch {
          return undefined;
        }
      })()
      : undefined;

    const process =
      previewProcessTitle || previewProcessDescription || (parsedProcessCards && parsedProcessCards.length > 0)
        ? {
          title: previewProcessTitle || "Unser Bewerbungsprozess",
          processCards: parsedProcessCards,
        }
        : undefined;

    return {
      jobOfferPreview: {
        _id: "preview",
        title: "Beispiel Stellenausschreibung",
        hourlySalary: "15",
        location: "12345",
        previewImageUrl: "",
        workingTimeModels: ["fullTime"],
      },
      organization: {
        _id: "1",
        name: "Pflegefachkraft",
        color: "#3182ce",
        logoUrl:
          "https://kandidat-io-integrations.s3.eu-central-1.amazonaws.com/public/226a8d07-5153-4196-a2f5-be4179013df5.file",
      },
      embedSettings: {
        heroEyebrowText: "Pflege mit Herz",
      },
      benefits: undefined,
      aboutUs: undefined,
      responsibilities: undefined,
      requirements: undefined,
      process,
      similarJobs: [],
    };
  }, []);

  const getJobPost = useCallback(async () => {
    if (isPreviewMode) {
      setJobPost(buildV2PreviewFromQueryParams());
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const res = await apiRequestRef.current<JobOfferEmbedDetailsShowingV2ResponseDto>(
      `embed/${organizationId}/job-offers/${id}`,
      "GET",
      {
        withoutAuth: true,
      },
      "v2.0",
    );
    if (res.data) {
      setJobPost(res.data);
      if (res.data.embedSettings) {
        setEmbedBaseSettings(res.data.embedSettings);
      } else {
        const listRes = await apiRequestRef.current<JobOfferEmbedListShowingResponseDto>(
          `embed/${organizationId}/job-offers`,
          "GET",
          { withoutAuth: true },
        );
        if (listRes.data?.embedSettings) {
          setEmbedBaseSettings(listRes.data.embedSettings);
        } else {
          setEmbedBaseSettings({ showImage: true });
        }
      }
    }
    setIsLoading(false);
  }, [buildV2PreviewFromQueryParams, id, isPreviewMode, organizationId]);

  useEffect(() => {
    if (debug) {
      setIsLoading(true);
      const timeoutId = window.setTimeout(() => {
        setJobPost(getMockJobPostV2());
        setIsLoading(false);
      }, 1500);
      return () => window.clearTimeout(timeoutId);
    }

    if (isLiveSettingsPreview) {
      setIsLoading(false);
      return;
    }

    getJobPost();
    return () => {
      // no-op
    };
  }, [debug, getJobPost, isLiveSettingsPreview]);

  return {
    isLiveSettingsPreview,
    sourceJobPost,
    isLoading,
    embedBaseSettings,
  };
}
